import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { BriefResponseDto } from '@inference.engine/workspace-api-client/web';
import { projectQuery } from '@/entities/project';
import { PROJECT_COMMENT_QUERY_KEYS } from '@/entities/project-comment';
import { notificationService } from '@/lib/services/notification.service';
import { createAgentComment } from '../api/agent-comments.api';
import { runBriefReview } from '../api/brief-review.api';
import { findTextRangeInTiptapDoc } from '../lib/find-text-range';
import { serializeBriefForReview } from '../lib/serialize-brief';
import { textToTiptap } from '../lib/text-to-tiptap';

/**
 * Runs the brief-reviewer-agent against a brief and posts one agent-authored
 * comment per suggestion. Fire-and-forget: failures surface as notifications,
 * never as thrown errors, so callers can trigger it from save flows safely.
 * The project scope is the platform project id supplied by the caller; the
 * brief's own `projectId` is the internal id and 403s against the project-
 * scoped comment routes.
 */
export function useBriefAgentReview() {
  const { t } = useTranslation('chats');
  const queryClient = useQueryClient();
  const [isReviewing, setIsReviewing] = useState(false);
  // Ref (not state) so back-to-back saves within one render can't race past
  // the guard: a second save while a review is in flight is dropped instead
  // of doubling every agent comment.
  const inFlightRef = useRef(false);

  const review = useCallback(
    async (brief: BriefResponseDto, platformProjectId: string) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const projectId = platformProjectId;
      setIsReviewing(true);
      try {
        // All audiences associated with the project review the brief.
        const project = await queryClient.fetchQuery(projectQuery(projectId));
        const audienceIds = (project.audiences ?? []).map((a) => a.id);
        const { suggestions, contextId } = await runBriefReview(
          serializeBriefForReview(brief, audienceIds),
        );
        if (suggestions.length === 0) return;

        // Post the suggestions concurrently — they're independent POSTs, so
        // serial awaits only stretched the in-flight window by suggestion count.
        const results = await Promise.allSettled(
          suggestions.map((s) => {
            // Anchor the comment to the quoted passage when the quote still
            // resolves verbatim inside the section — otherwise post unanchored.
            const sectionContent = brief.sections.find((sec) => sec.id === s.sectionId)?.content as
              | Record<string, unknown>
              | null
              | undefined;
            const range = s.highlightText
              ? findTextRangeInTiptapDoc(sectionContent, s.highlightText)
              : null;
            return createAgentComment(
              projectId,
              { targetType: 'brief', targetId: brief.id },
              {
                content: textToTiptap(s.comment),
                authorType: 'agent',
                agentId: s.sourceAgentId,
                contextId: contextId ?? undefined,
                suggestion: s.suggestedContent
                  ? { sectionId: s.sectionId, suggestedContent: textToTiptap(s.suggestedContent) }
                  : undefined,
                annotation: range
                  ? // eslint-disable-next-line i18next/no-literal-string -- annotation kind wire enum value
                    { kind: 'text', sectionId: s.sectionId, from: range.from, to: range.to }
                  : undefined,
              },
            );
          }),
        );
        const posted = results.filter((r) => r.status === 'fulfilled').length;
        if (posted < suggestions.length) {
          notificationService.showError(
            t('briefAgentReview.useBriefAgentReview.postedOfAgentSuggestions', {
              posted,
              total: suggestions.length,
            }),
          );
        }
        void queryClient.invalidateQueries({
          queryKey: PROJECT_COMMENT_QUERY_KEYS.list(projectId, 'brief', brief.id),
        });
        // Highlights hydrate from the annotations query, not the comment list.
        void queryClient.invalidateQueries({
          queryKey: PROJECT_COMMENT_QUERY_KEYS.annotations(projectId, 'brief', brief.id),
        });
      } catch (error) {
        notificationService.showError(t('briefAgentReview.useBriefAgentReview.agentReviewFailed'), {
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        inFlightRef.current = false;
        setIsReviewing(false);
      }
    },
    [queryClient, t],
  );

  return { review, isReviewing };
}
